import moment from 'moment'

/**
 * picker 禁用日期，开始日期和结束日期不能早于今天，且结束日期不能早于开始日期
 * @param currDate 当前日期
 * @param dateRang 日期范围
 * @returns {boolean} 是否禁用 true 禁用 false 不禁用
 */
export const disabledEndDate = (currDate, dateRang) => {
  const today = moment().startOf('day')
  const startDate = moment(dateRang?.[0])

  // 如果还没选开始日期，则不能选择今天之前的日期
  if (!startDate) {
    return currDate && currDate < today
  }
  // 已选开始日期，则结束日期不能早于开始日期且不能早于今天
  return currDate && (currDate < startDate.startOf('day') || currDate < today)
}

/**
 * 按月份分组数据
 * @param data 原始数据
 * @returns {{}} 按月份分组的对象
 */
export const getMonthGroup = (data) => {
  const monthlyData = data.reduce((acc, item) => {
    const date = new Date(item.type)
    const year = date.getFullYear()
    const month = date.getMonth() + 1 // 月份从0开始，所以加1

    const key = `${year}-${month.toString().padStart(2, '0')}`

    if (!acc[key]) {
      acc[key] = []
    }

    acc[key].push(item)
    return acc
  }, {})
  return monthlyData
}

/**
 * moment 获取对应前面的年月或后面的年月，如果传入-1指的是上一年月，如果是1指的是下一年月，默认未当前
 */
export const getYearMonthByNum = (num) => {
  return moment().add(num, 'months').format('YYYY-MM')
}

/**
 * 格式化指定moment年月
 * @param  {string} date - 年月字符串(如: 2025-12-22)
 * @param  {string} format ，默认为 'YYYY-MM-DD',还可以输入 'YYYY年MM月/DD日' 'YYYY/MM/DD'
 * @returns {string}
 */
export const formatYearMonthDate = (date, format = 'YYYY-MM-DD') => {
  return moment(date).format(format)
}

/**
 * 格式化指定moment年月
 * @param  {string} yearMonth - 年月字符串(如: 2025-12)
 * @param  {string} format ，默认为 'YYYY-MM',还可以输入 'YYYY年MM月' 'YYYY/MM'
 * @returns {string}
 */
export const formatYearMonth = (yearMonth, format = 'YYYY-MM') => {
  return moment(yearMonth).format(format)
}

/**
 * 获取指定年月的第一天和最后一天
 * @param {string} yearMonth - 年月字符串(如: 2025-12) YYYY-MM
 * @param {string} format - 日期格式，默认为 'YYYY-MM-DD',还可以输入 'YYYY年MM月DD日' 'YYYY/MM/DD'
 * @returns {Object} 包含第一天和最后一天的对象
 */
export const getMonthStartAndEnd = (yearMonth, format = 'YYYY-MM-DD') => {
  // 解析年月字符串，如 "2025-12"
  const dateParts = yearMonth.toString().split('-')
  if (dateParts.length !== 2) {
    throw new Error('年月格式必须为 YYYY-MM，如：2025-12')
  }

  const year = parseInt(dateParts[0])
  const month = parseInt(dateParts[1])

  // 验证月份范围
  if (month < 1 || month > 12) {
    throw new Error('月份必须在1-12之间')
  }

  // 创建指定年月的moment对象（注意moment中月份从0开始）
  const targetMonth = moment([year, month - 1])

  // 获取该月第一天
  const beginDate = targetMonth.clone().startOf('month').format(format)

  // 获取该月最后一天
  const endDate = targetMonth.clone().endOf('month').format(format)

  return {
    beginDate,
    endDate,
  }
}

/**
 * 判断传入的年月是否为之后月份
 * @param {string} yearMonth - 年月字符串，格式如 "2023-10"
 * @param {boolean} includeCurrent - 是否包含本月，默认true
 * @returns {boolean} - true: 符合条件; false: 不符合
 */
export const isYearMonthLater = (yearMonth, includeCurrent = true) => {
  const currentMonth = moment().format('YYYY-MM')
  const inputMonth = moment(yearMonth, 'YYYY-MM').format('YYYY-MM')
  // 如果格式固定为 YYYY-MM，可以直接用字符串比较
  if (includeCurrent) {
    // 包含本月：传入年月 >= 当前年月
    return inputMonth >= currentMonth
  } else {
    // 不包含本月：传入年月 > 当前年月
    return inputMonth > currentMonth
  }
}

/**
 * 获取指定周的周一到周日的日期范围
 * @param {number} weekOffset - 周偏移量，默认为0(本周)，-1为上周，1为下周
 * @param {string} format - 日期格式，默认为 'YYYY-MM-DD'
 * @returns {Array} 包含周一和周日日期的数组 [startDate, endDate]
 */
export const getWeekDateRange = (weekOffset = 0, format = 'YYYY-MM-DD') => {
  const startOfWeek = moment().add(weekOffset, 'weeks').startOf('isoWeek').format(format)
  const endOfWeek = moment().add(weekOffset, 'weeks').endOf('isoWeek').format(format)
  return [startOfWeek, endOfWeek]
}

/**
 * 获取指定月份的开始和结束日期范围
 * @param {number} monthOffset - 月份偏移量，默认为0(本月)，-1为上月，1为下月
 * @param {string} format - 日期格式，默认为 'YYYY-MM-DD'
 * @returns {Array} 包含月初和月末日期的数组 [startDate, endDate]
 */
export const getMonthDateRange = (monthOffset = 0, format = 'YYYY-MM-DD') => {
  const startOfMonth = moment().add(monthOffset, 'months').startOf('month').format(format)
  const endOfMonth = moment().add(monthOffset, 'months').endOf('month').format(format)
  return [startOfMonth, endOfMonth]
}

/**
 * 获取前 N 天到今天的日期范围
 * @param {number} days - 天数，默认为 14
 * @param {string} format - 日期格式，默认为 'YYYY-MM-DD'
 * @returns {Array} 包含开始日期和结束日期的数组 [startDate, endDate]
 */
export const getDaysDateRange = (days = 14, format = 'YYYY-MM-DD') => {
  const endDate = moment().format(format)
  const startDate = moment()
    .subtract(days - 1, 'days')
    .format(format)
  return [startDate, endDate]
}
